import { useState, useMemo, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import Sidebar, { useSidebarState, NAVIGATION, type NavigationItem } from './Sidebar'
import { useTheme } from '@/contexts/ThemeContext'
import { Menu, X, Settings, CpuIcon } from 'lucide-react'

/* ---------- Mobile Sidebar ---------- */

function MobileSidebar({ onClose }: { onClose: () => void }) {
  const { hiddenPages, pageOrder } = useTheme()

  // Mirror the desktop sidebar's filter + sort logic so the mobile menu
  // respects the user's Sidebar settings.
  const visibleNav = useMemo<NavigationItem[]>(() => {
    const mobileItems: NavigationItem[] = [...NAVIGATION, { name: 'Settings', to: '/settings', icon: Settings }]
    const filtered = mobileItems.filter((item) => !hiddenPages.includes(item.to))
    if (pageOrder.length === 0) return filtered
    return [...filtered].sort((a, b) => {
      const ai = pageOrder.indexOf(a.to)
      const bi = pageOrder.indexOf(b.to)
      if (ai === -1 && bi === -1) return 0
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  }, [hiddenPages, pageOrder])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex h-14 items-center justify-between px-4 border-b border-border">
        <div className="flex items-center gap-2">
          <CpuIcon className="h-7 w-7 text-primary" />
          <span className="text-base font-bold text-foreground">AI Agent Platform</span>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 flex flex-col gap-1 p-2 overflow-y-auto">
        {visibleNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-3">
        <div className="text-xs text-muted-foreground text-center">Version 1.0.0</div>
      </div>
    </div>
  )
}

/* ---------- Main Layout ---------- */

export default function AppLayout() {
  const { isCollapsed, toggleCollapsed } = useSidebarState()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // cmd+K (mac) / ctrl+K (linux/win) — focuses the orchestrator composer on
  // /dashboard. If we're not already there, route first; the composer marks
  // its textarea with `data-testid="orchestrator-composer-prompt"` so we can
  // grab + focus it after the next tick. Per PLAN-orchestrator-ux.md Phase A.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')
      if (!isCmdK) return
      // Don't hijack if user is in another input/textarea (search, inline edits).
      const target = e.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        // Allow cmd+K to still re-focus the composer if user is already inside it.
        if (target?.getAttribute('data-testid') !== 'orchestrator-composer-prompt') return
      }
      e.preventDefault()
      const focusComposer = () => {
        const el = document.querySelector<HTMLTextAreaElement>(
          '[data-testid="orchestrator-composer-prompt"]'
        )
        el?.focus()
      }
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard')
        // Wait one tick for the route + render before focusing.
        setTimeout(focusComposer, 100)
      } else {
        focusComposer()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate, location.pathname])

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transition-transform duration-300 lg:hidden',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <MobileSidebar onClose={() => setMobileMenuOpen(false)} />
      </div>

      {/* Desktop sidebar */}
      <Sidebar isCollapsed={isCollapsed} onToggleCollapsed={toggleCollapsed} />

      {/* Mobile-only hamburger button (floating top-left).
          On desktop the sidebar holds all user actions, so no top bar. */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="fixed top-3 left-3 z-30 lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-md border border-border bg-background/80 backdrop-blur text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Main content area */}
      <div
        className={cn(
          'transition-all duration-300 min-h-screen',
          isCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        )}
      >
        {/* Page content — extra top padding on mobile to clear the floating hamburger */}
        <main className="p-4 pt-14 lg:p-6 lg:pt-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
